import React, { useEffect, useState } from 'react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/auth'
import { userApi } from '@/api/users'

interface Staff { id: number; name: string; email: string; role: string; is_active: boolean }

const CallCenterStaffAdmin: React.FC = () => {
  const { user } = useAuthStore()
  const teamId = (user as any)?.team_id as number | undefined
  const [staff, setStaff] = useState<Staff[]>([])
  const [notes, setNotes] = useState<any[]>([])
  const [selectedAgent, setSelectedAgent] = useState<number | ''>('')

  const fetchStaff = async () => {
    if (!teamId) return
    try {
      const [agentsActive, adminsActive, agentsInactive, adminsInactive] = await Promise.all([
        apiClient.get('/api/v1/users', { params: { role: 'staff', is_active: true, page_size: 100, team_id: teamId } }),
        apiClient.get('/api/v1/users', { params: { role: 'callcenter_admin', is_active: true, page_size: 100, team_id: teamId } }),
        apiClient.get('/api/v1/users', { params: { role: 'staff', is_active: false, page_size: 100, team_id: teamId } }),
        apiClient.get('/api/v1/users', { params: { role: 'callcenter_admin', is_active: false, page_size: 100, team_id: teamId } }),
      ])
      const normalize = (arr:any[]) => (arr || []).map((u:any)=>({ id: u.id, name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email, email: u.email, role: u.role, is_active: u.is_active }))
      const merged = [
        ...normalize(agentsActive.data?.users),
        ...normalize(adminsActive.data?.users),
        ...normalize(agentsInactive.data?.users),
        ...normalize(adminsInactive.data?.users),
      ]
      setStaff(merged)
    } catch { toast.error('Personel getirilemedi') }
  }

  useEffect(()=>{ fetchStaff() },[teamId])

  const toggleActive = async (s: Staff) => {
    try {
      await userApi.update(s.id, { is_active: !s.is_active } as any)
      toast.success('Durum güncellendi')
      fetchStaff()
    } catch (e:any) { toast.error(e?.message || 'Güncellenemedi') }
  }

  const fetchNotes = async () => {
    if (!selectedAgent) return
    try {
      const res = await apiClient.get(`/api/v1/callcenter/agents/${selectedAgent}/notes`)
      setNotes(res.data?.data || [])
    } catch { toast.error('Notlar getirilemedi') }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="pb-5 border-b border-slate-200">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Callcenter</p>
        <h1 className="text-2xl font-serif font-medium text-slate-900">Personel Yönetimi</h1>
        <p className="text-slate-600 mt-1">Personeli aktif/pasif yapın, özet ve notlarını görüntüleyin</p>
      </div>

      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Personel Listesi</h2>
        {staff.length === 0 ? (
          <p className="text-slate-500">Kayıt yok</p>
        ) : (
          <div className="space-y-2">
            {staff.map(s => (
              <div key={s.id} className={`p-3 border flex items-center justify-between ${s.is_active ? 'border-slate-200' : 'bg-danger-50 border-danger-200'}`}>
                <div>
                  <p className="font-medium text-slate-900">{s.name}</p>
                  <p className="text-xs text-slate-500">{s.email} • {s.role}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={()=>{setSelectedAgent(s.id); fetchNotes()}}>Notları Gör</Button>
                  <Button variant="outline" className={s.is_active ? '' : 'text-danger-600'} onClick={()=>toggleActive(s)}>{s.is_active ? 'Pasifleştir' : 'Aktifleştir'}</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Personel Notları</h2>
        <div className="flex items-center gap-2 mb-3">
          <select
            value={selectedAgent}
            onChange={(e)=>setSelectedAgent(Number(e.target.value))}
            className="px-3 py-2 border border-slate-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Personel seç</option>
            {staff.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
          <Button variant="outline" onClick={fetchNotes} disabled={!selectedAgent}>Yükle</Button>
        </div>
        {notes.length === 0 ? (
          <p className="text-slate-500">Kayıt yok</p>
        ) : (
          <div className="space-y-2">
            {notes.map((n:any)=> (
              <div key={n.id} className="p-3 border border-slate-200">
                <p className="font-medium text-slate-900">{n.note_text}</p>
                <p className="text-xs text-slate-500">Müşteri: {n.customer_full_name ? `${n.customer_full_name} (ID #${n.customer_id})` : `#${n.customer_id}`} • {n.created_at}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CallCenterStaffAdmin
